import type { FetcherOptions, FetcherResponse } from './fetcher';
import type { KeyedMutator, SWRConfiguration, Key as SWRKey } from 'swr';

import cloneDeep from 'lodash/cloneDeep'; // eslint-disable-line you-dont-need-lodash-underscore/clone-deep
import { useContext, useMemo } from 'react';
import useSWR from 'swr';

import { APIBaseUrlContext } from '@core/context';

import fetcher from './fetcher';
import useUnauthorizedRedirect from './useUnauthorizedRedirect';

export type Options = Parameters<typeof fetcher>[1] & {
  /**
   * Specifies a base URL that is prepended to the `path` when constructing the
   * request URL. By default, a base URL is derived from `APIBaseUrlContext`,
   * which is usually set for child projects of an enterprise group.
   *
   * If base URL already exists in the `path` argument, the base URL provided in
   * this option or `APIBaseUrlContext` is ignored and not prepended again.
   */
  apiBaseUrl?: string;
  swr?: SWRConfiguration;
  useLegacyBaseUrl?: boolean;
};

/**
 * A hook that wraps useSWR to get the caching and perfomance benefits of SWR
 * when reading data from the Readme API.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useReadmeApi<Data, Error = any>(path: string | null, options?: Options | null, isReady = true) {
  const legacyApiBaseUrl = useContext(APIBaseUrlContext);
  // Extract SWR options out of the options hash.
  const {
    apiBaseUrl: optionApiBaseUrl,
    swr: swrOptions = {},
    useLegacyBaseUrl = true,
    ...fetcherOptions
  } = options || {};

  const baseUrl = optionApiBaseUrl || (useLegacyBaseUrl ? legacyApiBaseUrl : '');

  let normalizedPath = path;
  if (typeof path === 'string') {
    // Ensure path always begins with a leading slash.
    normalizedPath = path.startsWith('/') ? path : `/${path}`;
    // Ensure path begins with the base URL.
    normalizedPath = baseUrl && normalizedPath.startsWith(baseUrl) ? normalizedPath : `${baseUrl}${normalizedPath}`;
  }

  // NOTE: These SWR options are using default values different than SWRs.
  // https://swr.vercel.app/docs/options#options
  swrOptions.revalidateOnMount ??= true;
  swrOptions.revalidateOnFocus ??= false;
  swrOptions.shouldRetryOnError ??= false;

  const requestUrl = normalizedPath && isReady ? normalizedPath : null;

  /**
   * Generates the SWR cache key that'll be used to initiate the request using
   * our custom fetcher. To optimize SWR cache hit-rates, avoid using an Array
   * object as the key unless we absolutely need to pass along `fetcherOptions`
   * to our custom `fetcher`.
   */
  const swrKey = useMemo<SWRKey>(() => {
    if (!requestUrl) return null;
    if (!Object.keys(fetcherOptions).length) return requestUrl;
    return [requestUrl, fetcherOptions] satisfies [string, FetcherOptions];
  }, [fetcherOptions, requestUrl]);

  const { data, error, mutate, isValidating } = useSWR<FetcherResponse<Data>, Error>(swrKey, fetcher, swrOptions);

  const responseHeaders = data?.__headers__ || {};

  /** Cleaned data with internal properties removed. */
  const cleanData = useMemo<Data | undefined>(() => {
    if (!data) return undefined;
    const nextData = cloneDeep(data);
    delete nextData.__headers__;
    return nextData;
  }, [data]);

  /** Cleaned mutate function without internal properties. */
  const cleanMutate = mutate as KeyedMutator<Data>;

  useUnauthorizedRedirect(error);

  return {
    data: cleanData,
    error,

    /**
     * ETag header value returned from the last request if it exists. This value
     * can be used to send conditional requests using the `If-None-Match` header
     * to optimize bandwidth on large resources that don't change often. When
     * response doesn't include an Etag header, this value will be `null`.
     */
    etag: responseHeaders.etag,

    /**
     * True when fetching is complete and a valid `data` or `error` object has
     * been set, allowing either to be parsed and displayed to the user. Use
     * this property to conditionally display a loading state in the UI.
     */
    isLoading: normalizedPath && isReady ? !error && !data : false,

    /**
     * True if there's a request or revalidation loading via SWR
     */
    isValidating,

    /**
     * Use this to perform mutations on the current data set. See
     * https://swr.vercel.app/docs/mutation for more patterns on how to
     * configure optimistic updates and auto-rollbacks on error.
     */
    mutate: cleanMutate,

    /**
     * Returns reference to the SWR request key that was used to fetch data.
     * This key is used by the SWR cache during `mutate()` calls and can be used
     * by the global `mutate` function to override cached data from the internal
     * `useSWR` hook here.
     */
    swrKey,
  };
}

export { default as useReadmeApiNext } from './next';
export { default as useReadmeApiInfinite } from './infinite';
export { default as useReadmeApiWrite } from './write';
export { fetcher };
export default useReadmeApi;
